import { DateTime } from 'luxon';
import Dinero from 'dinero.js';
import { OrderType } from '../order/order';
import { ItemType } from '../accounting/accounting';

export type TaxCodeRow = {
  id: string;
  name: string;
  createdAt: DateTime;
  deletedAt?: DateTime;
  qboTaxCodeId?: string;
};

export type TaxJurisdiction = {
  id: string;
  name: string;
  percentage: number;
  createdAt: DateTime;
  deletedAt?: DateTime;
  source: 'manual' | 'TaxJar';
  linkId?: string;
  generalLedgerAccountId?: string;
  taxCategories: TaxCategory[];
};

export type TaxCode = {
  name: string;
  id: string;
  source: string;
  totalPercentage: number;
  jurisdictions: TaxJurisdiction[];
  qboTaxCodeId?: string;
};

export type SearchTaxCodesRequest = {
  keyword?: string;
  ids?: string[];
  deleted?: boolean;
  page?: number;
  pageSize?: number;
  hasQBOId?: boolean;
};

export type SearchTaxJurisdictionsRequest = {
  taxJurisdictionName?: string;
  deleted?: boolean;
  source?: 'manual' | 'TaxJar';
};

export type TaxCodeRequest = Omit<TaxCodeRow, 'id' | 'createdAt' | 'deletedAt'>;

export type TaxBreakdownJson = {
  jurisdiction: string;
  percentage: number;
  amount: Dinero.Options;
  jurisdictionId: string;
  source: string;
};

export type TaxBreakdown = {
  jurisdiction: string;
  percentage: number;
  amount: Dinero.Dinero;
  jurisdictionId: string;
  source: string;
};

export type TaxLocationLink = {
  id: string;
  locationId: number;
  taxCodeId: string;
  shippingType: string;
  createdAt: DateTime;
  deletedAt?: DateTime;
};
export function toTaxBreakdown(input: TaxBreakdownJson): TaxBreakdown {
  return {
    percentage: input.percentage,
    jurisdiction: input.jurisdiction,
    amount: Dinero(input.amount),
    jurisdictionId: input.jurisdictionId,
    source: input.source,
  };
}

export function getTotalFromTotalPercentage(taxCharged: Dinero.Dinero, taxPercentages: number[]) {
  return taxCharged.divide(taxPercentages.reduce((sum, v) => sum + v / 100, 0));
}

export function getPartialTaxFromTotal(taxCharged: Dinero.Dinero, taxJurisdictions: TaxJurisdiction[]): TaxBreakdown[] {
  const total = getTotalFromTotalPercentage(
    taxCharged,
    taxJurisdictions.map((j) => j.percentage),
  );
  return taxJurisdictions.map((j) => {
    return {
      amount: total.multiply(j.percentage / 100),
      jurisdiction: j.name,
      jurisdictionId: j.id,
      percentage: j.percentage,
      source: j.source,
    };
  });
}

export type TaxCategory = {
  id: string;
  name: string;
  description?: string;
  qboTaxCodeId?: string;
  createdAt: DateTime;
  deletedAt?: DateTime;
};

export type ItemTaxCategory = {
  id: string;
  itemId?: string | number;
  itemType: ItemType;
  transactionType?: OrderType;
  taxCategoryId?: string;
  createdAt: DateTime;
  deletedAt?: DateTime;
};

export enum TaxConfigType {
  CustomerZipCode,
  LocationZipCode,
  Global,
  City,
  Avalara,
  TaxRate,
}
